import "./text-box-infographic.scss";
import { useEffect, useRef, useState } from "react";
import ReactQuill from 'react-quill-new';
import 'quill/dist/quill.snow.css';
import { toolbarConfig } from "../../../../config/infographic.config";
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ICON_AI from "../../../../assets/images/icon-ai.png";
import ICON_CORRECT from "../../../../assets/images/check-green.svg";
import ICON_EDIT from "../../../../assets/images/edit.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import InfographicService from "../../../../services/infographic.service";
import { useSnackbar } from "../../../../utility/snackbar.utility";
import CustomSnackbar from "../../../snackbar/snackbar.component";

const TextBoxInfographicComponent = ({ box, templateText, spellcheckActiveId, isLoading, onUpdateBox, onUpdateSpellcheck, onUpdateSpellcheckLoading }) => {

    const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

    const textEditorRef = useRef<ReactQuill>(null);
    const hasRun = useRef(false);

    const [spellcheckContent, setSpellcheckContent] = useState('');

    const isActive = spellcheckActiveId === box.id;
    const isDisabled = isLoading && !isActive;

    useEffect(() => {
        if (textEditorRef.current && !hasRun.current) {
            const editor = textEditorRef.current.getEditor();

            setTimeout(() => {
                editor.format('size', '16px', 'silent');
                editor.history.clear();

                requestAnimationFrame(() => {
                    editor.blur();
                    window.scrollTo({ top: -1000, behavior: 'smooth' });
                });

            }, 0);

            hasRun.current = true;
        }
    }, []);

    const updateTextBox = (content: any, source: string) => {
        if (source !== 'user') return;
        onUpdateBox({ ...box, text: content });
    };

    const handleSpellcheck = async () => {
        onUpdateSpellcheck(box.id);
        if (box.id === spellcheckActiveId) return;

        onUpdateSpellcheckLoading(true);

        try {
            const response = await InfographicService.checkSpelling(box.text);
            setSpellcheckContent(response.data.html);
        } catch (error) {
            onUpdateSpellcheckLoading(false);
        } finally {
            onUpdateSpellcheckLoading(false);
        }
    }

    const handleApply = () => {
        onUpdateBox({ ...box, text: removeBackgroundColor(spellcheckContent) });
        onUpdateSpellcheck(null);
        showSnackbar('นำไปใช้เรียบร้อยแล้ว');
    }

    const removeBackgroundColor = (htmlString: string) => {
        return htmlString.replace(/<span style=['"][^>]*background-color: #00B69B20[^>]*>(.*?)<\/span>/g, "$1");
    };

    return (
        <>
            <div>
                {
                    templateText.groupStart &&
                    <div className='mt-3 mb-2 text-box-title'>
                        <p>
                            {
                                (box.id === 1) ? 'Headline ของ Infographic' : `เนื้อหากล่องที่ ${templateText.order}`
                            }
                        </p>
                    </div>
                }

                <div className='position-relative text-box-quill'>
                    <ReactQuill
                        ref={textEditorRef}
                        value={box.text}
                        onChange={(content, delta, source) => updateTextBox(content, source)}
                        modules={{
                            toolbar: toolbarConfig
                        }}
                        placeholder="Edit text here..."
                        style={{ maxWidth: "auto", minHeight: "100px" }}
                    />

                    <div className='text-box-button-check'>
                        <Button variant="text" onClick={handleSpellcheck} disabled={isDisabled}>
                            {
                                (isLoading && isActive) ?
                                    <CircularProgress className='text-box-progress me-1' /> :
                                    <img src={ICON_AI} className='me-1' width={20} height={19} />
                            }

                            <span className={`text-box-spellcheck-button ${isDisabled ? 'text-box-spellcheck-button-disabled' : ''}`}>ตรวจตัวสะกด</span>
                        </Button>
                    </div>

                    {
                        (isActive && !isLoading) &&
                        <div className='spellcheck-box ql-editor'>
                            <FontAwesomeIcon icon={faClose} className="spellcheck-box-close" onClick={() => onUpdateSpellcheck(null)}></FontAwesomeIcon>

                            <div className='d-flex align-items-center mb-2'>
                                <img src={ICON_CORRECT} width={20} height={20} className='me-2' />
                                <span className='spellcheck-box-title'>ตรวจสอบเรียบร้อยแล้ว</span>
                            </div>

                            <div
                                className='spellcheck-box-content mb-2'
                                dangerouslySetInnerHTML={{ __html: spellcheckContent }}>
                            </div>

                            <Button
                                variant="text"
                                className='spellcheck-box-button'
                                onClick={handleApply}
                            >
                                <img src={ICON_EDIT} width={17} height={17} className='me-1' />
                                <span>นำไปใช้</span>
                            </Button>
                        </div>
                    }
                </div>

                {
                    (box.id === 1) && <Divider className='my-3' />
                }
            </div>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                duration={snackbar.duration}
                onClose={hideSnackbar}
            />
        </>
    )
}

export default TextBoxInfographicComponent;